import { useState, useEffect, useCallback } from 'react';
import { X, Send, Tag, ChevronRight, Loader2, Coins } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { NICHE_IMAGES } from '../../utils/constants';
import { wixCommentsApi } from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

// Format timestamp to readable date/time
const formatDateTime = (timestamp, dateStr) => {
  try {
    const date = timestamp ? new Date(timestamp) : dateStr ? new Date(dateStr) : null;
    if (!date || isNaN(date.getTime())) return 'Just now';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Show full date for older messages
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Just now';
  }
};

// Get random memoji avatar based on gender
const getGenderAvatar = (gender) => {
  const femaleMemojis = ['/avatars/Female-memoji-1.svg', '/avatars/Female-memoji-2.svg', '/avatars/Female-memoji-3.svg', '/avatars/Female-memoji-4.svg'];
  const maleMemojis = ['/avatars/Male-memoji-5.svg', '/avatars/Male-memoji-6.svg', '/avatars/Male-memoji-7.svg'];
  const avatarList = (gender || '').toLowerCase() === 'female' ? femaleMemojis : maleMemojis;
  return avatarList[Math.floor(Math.random() * avatarList.length)];
};

export default function GroupChatDrawer() {
  const {
    selectedGroup,
    setSelectedGroup,
    profileForm,
    setShowGroupDealsDrawer,
    groupChatRefreshTrigger,
  } = useAppState();
  const { isGuestMode, setShowGuestPrompt, user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedItemId, setFeedItemId] = useState(null);

  // Lock body scroll when drawer is open (must be before early return)
  useBodyScrollLock(!!selectedGroup);

  // Fetch comments from Wix API
  const fetchGroupComments = useCallback(async () => {
    if (!selectedGroup) return;

    // Use groupId directly as feedItemId
    const groupId = selectedGroup.uuid || selectedGroup.group_uuid || selectedGroup.id;
    if (!groupId) return;

    setLoading(true);
    setFeedItemId(groupId);

    try {
      // Fetch comments using groupId as feedItemId
      const { comments } = await wixCommentsApi.getComments(groupId);

      // Map and strip [test] marker from display text
      const mappedMessages = comments.map(comment => ({
        id: comment.id,
        author: comment.author,
        // Use local memoji if avatar is a dicebear URL or missing
        avatar: comment.avatar && !comment.avatar.includes('dicebear')
          ? comment.avatar
          : getGenderAvatar(comment.gender),
        // Strip [test] marker from display text
        text: (comment.text || '').replace(/\s*\[test\]\s*$/i, ''),
        // Show actual date/time
        time: formatDateTime(comment.timestamp, comment.date),
        type: comment.author === 'bonndleagent' ? 'cashback_announcement' : 'message',
      }));

      setMessages(mappedMessages);
    } catch (error) {
      console.error('Failed to fetch group comments:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  // Fetch comments when group is selected
  useEffect(() => {
    fetchGroupComments();
  }, [fetchGroupComments]);

  // Refresh comments when trigger changes (after successful payment)
  useEffect(() => {
    if (groupChatRefreshTrigger && selectedGroup) {
      fetchGroupComments();
    }
  }, [groupChatRefreshTrigger, selectedGroup, fetchGroupComments]);

  if (!selectedGroup) return null;

  // Use deals from the group data (from API response via getOne)
  // Each deal has: merchant_name, merchant_logo, deal_percentage, uuid, etc.
  const deals = selectedGroup.deals || [];

  // Fallback to merchants array for backwards compatibility
  const merchants = selectedGroup.merchants || [];

  // Use deals if available, otherwise fall back to merchants
  const activeDeals = deals.length > 0 ? deals : merchants;

  // Get member count from members array or member_count field
  const memberCount = selectedGroup.members?.length || selectedGroup.member_count || 0;

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    // Guest users need to sign up to send messages
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');

    // Gender-based avatar using local memojis
    const userGender = (profileForm?.gender || user?.gender || '').toLowerCase();
    const userAvatar = getGenderAvatar(userGender);

    // Optimistically add message to local state
    const optimisticMessage = {
      id: Date.now(),
      author: profileForm?.bonndleTag || user?.bold_tag || profileForm?.firstName || 'You',
      avatar: userAvatar,
      text: messageText,
      time: 'Just now',
      type: 'message',
    };
    setMessages(prev => [...prev, optimisticMessage]);

    // Post to Wix API if we have a feed item
    if (feedItemId) {
      try {
        await wixCommentsApi.addComment(feedItemId, {
          author: profileForm?.bonndleTag || user?.bold_tag || profileForm?.firstName || 'User',
          avatar: userAvatar,
          text: messageText,
          userId: user?.uuid || user?.id || null,
        });
      } catch (error) {
        console.error('Failed to post message to Wix:', error);
      }
    }
  };

  const handleClose = () => {
    setSelectedGroup(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={handleClose} />

      {/* Full Screen Panel */}
      <div className="slide-panel">
        <div>
          {/* Header - Fixed */}
          <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <img
                src={(selectedGroup.name || '').toLowerCase().includes('bonndle')
                  ? '/logo/bonndleLogoIcon_Indigo.svg'
                  : selectedGroup.image_url || selectedGroup.avatar || NICHE_IMAGES[selectedGroup.niche]?.[0] || NICHE_IMAGES.general[0]}
                alt={selectedGroup.name}
                className="w-10 h-10 rounded-xl object-cover"
              />
              <div>
                <p className="font-bold text-gray-900">{selectedGroup.name}</p>
                <p className="text-xs text-gray-500">
                  {memberCount} members
                  {activeDeals.length > 0 && ` • ${activeDeals.length} Active Deals`}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Active Deals Banner - Shows deals from group */}
          {activeDeals.length > 0 && (
            <button
              onClick={() => setShowGroupDealsDrawer(true)}
              className="flex items-center gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left"
            >
              {/* Stacked Merchant Logo Avatars */}
              <div className="flex -space-x-3">
                {activeDeals.slice(0, 4).map((deal, index) => (
                  <img
                    key={deal.merchant_name || deal.uuid || index}
                    src={deal.merchant_logo}
                    alt={deal.merchant_name}
                    className="w-8 h-8 rounded-full border-2 border-white object-contain shadow-sm bg-white p-0.5"
                    style={{ zIndex: 4 - index }}
                    onError={(e) => {
                      // Fallback to initials on image error
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
              <span className="flex-1" />
              <span className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap flex items-center gap-1">
                View Deals
                <ChevronRight size={14} />
              </span>
            </button>
          )}

          {/* Messages - Scrollable */}
          <div className="slide-panel-content p-4 space-y-3 bg-gray-50">
            {loading ? (
              /* Loading State */
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-indigo-600" />
              </div>
            ) : messages.length === 0 ? (
              /* Empty State */
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Start coordinating with your group!</p>
                <p className="text-xs text-gray-400 mt-1">Messages will appear here</p>
              </div>
            ) : (
              /* Messages List */
              messages.map((msg) => {
                const isBonndleAgent = msg.author === 'bonndleagent';
                return (
                  <div key={msg.id} className="flex gap-3">
                    {/* Avatar - Coin icon on indigo circle for bonndleagent, memoji for users */}
                    {isBonndleAgent ? (
                      <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                        <Coins size={16} className="text-white" />
                      </div>
                    ) : (
                      <img
                        src={msg.avatar}
                        alt={msg.author}
                        className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold text-sm ${isBonndleAgent ? 'text-indigo-600' : 'text-gray-900'}`}>
                          {msg.author}
                        </span>
                        {msg.type === 'deal_comment' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full">
                            <Tag size={10} />
                            {msg.dealBrand}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{msg.time}</span>
                      </div>
                      {/* Message bubble - light indigo for bonndleagent, white for users */}
                      <div className={`rounded-2xl rounded-tl-sm p-3 shadow-sm ${
                        isBonndleAgent ? 'bg-indigo-50 border border-indigo-100' : 'bg-white'
                      }`}>
                        <p className={`text-sm ${isBonndleAgent ? 'text-indigo-700' : 'text-gray-700'}`}>
                          {msg.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input - Fixed at bottom */}
          <div className="slide-panel-footer p-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Message group..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
